import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Row, Col, Container } from "react-bootstrap";
import styled from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faCircleCheck,
  faPen,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import {
  createFiscalAddress,
  getFiscalAddressList,
  deleteFiscalAddress,
  updateFiscalAddress,
} from "../actions/userActions";
import {
  ErrorMessageBox,
  ModernButton,
  ModalOverlay,
} from "../components/DefaultImports";
import { CustomModal } from "../components/CustomModal";
import { ConfirmationModal } from "../components/ConfirmationModal";
import InputMask from "react-input-mask";

/* ====== styles ====== */
const Title = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: #99b0ad;
  margin-bottom: 8px;
`;

const Grid = styled(Row)`
  row-gap: 12px;
`;

const CardBox = styled.div`
  position: relative;
  width: 100%;
  padding: 14px;
  min-height: 150px;
  background: #ffffff;
  color: #1c1c1c;
  border: ${({ isSelected }) =>
    isSelected ? "2px solid #de8c3a" : "1px solid #e7ecec"};
  border-radius: 12px;
  box-shadow: 0 1px 8px rgba(0, 0, 0, 0.06);
  transition: transform 0.12s ease, box-shadow 0.12s ease, border-color 0.12s;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 3px 12px rgba(0, 0, 0, 0.08);
  }

  cursor: ${({ isItSelectable, onClick }) =>
    isItSelectable || onClick ? "pointer" : "default"};
`;

const AddCard = styled(CardBox)`
  display: flex;
  align-items: center;
  justify-content: center;
  border-style: dashed;
  border-color: #99b0ad;
  color: #99b0ad;

  &:hover {
    border-color: #de8c3a;
    color: #de8c3a;
  }
`;

const BigPlus = styled(FontAwesomeIcon)`
  font-size: 46px;
`;

const CheckIcon = styled(FontAwesomeIcon)`
  position: absolute;
  font-size: 20px;
  left: 8px;
  top: 8px;
  color: #de8c3a;
`;

const SelectCircle = styled.div`
  position: absolute;
  width: 20px;
  height: 20px;
  left: 8px;
  top: 8px;
  background-color: white;
  border: 1px solid #cfcfcf;
  border-radius: 50%;
`;

const Actions = styled.div`
  position: absolute;
  top: 8px;
  right: 8px;
  display: flex;
  gap: 6px;
`;

const IconButton = styled.button`
  border: none;
  background: transparent;
  padding: 6px;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  color: #99b0ad;
  cursor: pointer;

  &:hover {
    background: #f5f7f7;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const Name = styled.div`
  font-weight: 700;
  font-size: 16px;
  color: #334442;
  margin-bottom: 6px;
  padding-left: ${(p) => (p.$hasSelector ? "28px" : "0")};
`;

const Line = styled.div`
  color: #556663;
  font-size: 14px;
  line-height: 1.35;
  padding-left: ${(p) => (p.$hasSelector ? "28px" : "0")};
`;

const ScrollContainer = styled.div`
  width: 100%;
  max-height: 420px;
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 8px;
  scrollbar-gutter: stable;
`;

const EmptyState = styled.div`
  background: #faf6e9;
  border: 1px dashed #e7d7bf;
  color: #de8c3a;
  padding: 14px;
  border-radius: 12px;
  text-align: center;
  font-weight: 600;
  margin-bottom: 10px;
`;

/* Simple variant styles */
const SimpleBlock = styled.div`
  margin-bottom: 18px;
`;

const SimpleRow = styled.div`
  font-size: 15px;
  line-height: 1.35;
  color: #334442;
  margin: 4px 0;
`;

const SimpleLabel = styled.span`
  font-weight: 700;
  color: #99b0ad;
  margin-right: 6px;
`;

/* form styles */
const StyledInput = styled.input`
  width: 100%;
  padding: 12px 16px;
  margin-bottom: 18px;
  font-size: 16px;
  border: 1px solid ${(props) => (props.invalid ? "#dc3545" : "#ddd")};
  border-radius: 10px;
  background-color: #fafafa;
  transition: all 0.2s ease;
  &:focus {
    border-color: ${(props) => (props.invalid ? "#dc3545" : "#de8c3a")};
    background-color: #fff;
    box-shadow: ${(props) =>
      props.invalid
        ? "0 0 0 4px rgba(220, 53, 69, 0.15)"
        : "0 0 0 4px rgba(222, 140, 58, 0.15)"};
    outline: none;
  }
  &::placeholder {
    color: #aaa;
  }
`;

const StyledMaskedInput = styled(InputMask)`
  width: 100%;
  padding: 12px 16px;
  margin-bottom: 18px;
  font-size: 16px;
  border: 1px solid ${(props) => (props.invalid ? "#dc3545" : "#ddd")};
  border-radius: 10px;
  background-color: #fafafa;
  transition: all 0.2s ease;
  &:focus {
    border-color: ${(props) => (props.invalid ? "#dc3545" : "#de8c3a")};
    background-color: #fff;
    box-shadow: ${(props) =>
      props.invalid
        ? "0 0 0 4px rgba(220, 53, 69, 0.15)"
        : "0 0 0 4px rgba(222, 140, 58, 0.15)"};
    outline: none;
  }
  &::placeholder {
    color: #aaa;
  }
`;

const FieldLabel = styled.h5`
  font-size: 14px;
  font-weight: 700;
  color: #99b0ad;
  margin: 0 0 6px;
`;

/* ====== form ====== */
const FiscalAddressForm = ({
  user_id,
  dispatch,
  getFiscalAddressList,
  closeModal,
  initialData = null, // <-- when editing, pass the address object
}) => {
  const [formData, setFormData] = useState({
    id: null,
    user: user_id,
    name: "",
    address: "",
    postal_code: "",
    country: "",
    floor_number: "",
    fiscal_number: "",
  });
  const [error, setError] = useState("");
  const [invalidFields, setInvalidFields] = useState([]);

  // hydrate form when editing
  useEffect(() => {
    if (initialData) {
      setFormData({
        id: initialData.id ?? null,
        user: user_id,
        name: initialData.name || "",
        address: initialData.address || "",
        postal_code: initialData.postal_code || "",
        country: initialData.country || "",
        floor_number: initialData.floor_number || "",
        fiscal_number: initialData.fiscal_number || "",
      });
    } else {
      setFormData((s) => ({
        ...s,
        id: null,
        user: user_id,
        name: "",
        address: "",
        postal_code: "",
        country: "",
        floor_number: "",
        fiscal_number: "",
      }));
    }
  }, [initialData, user_id]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((s) => ({ ...s, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const required = [
      { key: "name", label: "Nome", value: formData.name },
      { key: "address", label: "Morada", value: formData.address },
      {
        key: "postal_code",
        label: "Código Postal",
        value: formData.postal_code,
      },
      { key: "country", label: "País", value: formData.country },
      { key: "fiscal_number", label: "NIF", value: formData.fiscal_number },
    ];

    const missing = required.filter((f) => !String(f.value || "").trim());
    if (missing.length) {
      /* setError(
        `Por favor, preencha os seguintes campos obrigatórios: ${missing
          .map((f) => f.label)
          .join(", ")}.`
      ); */
      setInvalidFields(missing.map((f) => f.key));
      return;
    }

    setError("");
    setInvalidFields([]);

    const payload = {
      user: user_id,
      name: formData.name.trim(),
      address: formData.address.trim(),
      postal_code: formData.postal_code.trim(),
      country: formData.country.trim(),
      floor_number: formData.floor_number?.trim() || "",
      fiscal_number: formData.fiscal_number.trim(),
    };

    if (formData.id) {
      // UPDATE
      await dispatch(updateFiscalAddress(formData.id, payload));
    } else {
      // CREATE
      await dispatch(createFiscalAddress(user_id, payload));
    }

    await dispatch(getFiscalAddressList(user_id));
    closeModal();
  };

  return (
    <>
      {/* Optional error banner */}
      {error && (
        <ErrorMessageBox style={{ fontSize: "0.9rem", marginBottom: 10 }}>
          {error}
        </ErrorMessageBox>
      )}

      <form id="fiscalAddressForm" onSubmit={handleSubmit}>
        <div className="form-group">
          <FieldLabel>Nome</FieldLabel>
          <StyledInput
            type="text"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            placeholder="Nome fiscal"
            invalid={invalidFields.includes("name")}
          />
        </div>
        <div className="form-group">
          <FieldLabel>NIF (Número de Identificação Fiscal)</FieldLabel>
          <StyledInput
            type="text"
            name="fiscal_number"
            value={formData.fiscal_number}
            onChange={handleInputChange}
            placeholder="123456789"
            invalid={invalidFields.includes("fiscal_number")}
          />
        </div>
        <div className="form-group">
          <FieldLabel>Morada</FieldLabel>
          <StyledInput
            type="text"
            name="address"
            value={formData.address}
            onChange={handleInputChange}
            placeholder="Morada fiscal"
            invalid={invalidFields.includes("address")}
          />
        </div>
        <div className="form-group">
          <FieldLabel>Código Postal</FieldLabel>
          <StyledMaskedInput
            mask="9999-999"
            placeholder="____-___"
            maskChar="_"
            value={formData.postal_code}
            onChange={handleInputChange}
            name="postal_code"
            invalid={invalidFields.includes("postal_code")}
          />
        </div>
        <div className="form-group">
          <FieldLabel>País</FieldLabel>
          <StyledInput
            type="text"
            name="country"
            value={formData.country}
            onChange={handleInputChange}
            placeholder="País"
            invalid={invalidFields.includes("country")}
          />
        </div>
        <div className="form-group">
          <FieldLabel>Informação adicional (opcional)</FieldLabel>
          <StyledInput
            type="text"
            name="floor_number"
            value={formData.floor_number}
            onChange={handleInputChange}
            placeholder="Andar, porta, bloco"
          />
        </div>
        <div className="modal-footer" style={{ padding: 0 }}>
          <ModernButton color="#de8c3a" fontSize="14px" type="submit">
            Guardar
          </ModernButton>
        </div>
      </form>
    </>
  );
};

/* ====== main ====== */
function FiscalAdress({
  isItSelectable = false,
  currentSelected = null,
  setSelected,
  showTitle = true,
  cardsPerRow = 2,
  variant = "default", // "default" (cards) | "simple" (text-only)
}) {
  const [showModal, setShowModal] = useState(false);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [addressToDelete, setAddressToDelete] = useState(null);
  const [editingAddress, setEditingAddress] = useState(null); // <-- NEW

  const dispatch = useDispatch();
  const { userInfo } = useSelector((state) => state.userLogin);
  const { getFiscalAdress } = useSelector((state) => state.fiscalAddressStore);

  const hasAny = Array.isArray(getFiscalAdress) && getFiscalAdress.length > 0;

  useEffect(() => {
    if (userInfo?.id) dispatch(getFiscalAddressList(userInfo.id));
  }, [dispatch, userInfo?.id]);

  const handleSelection = (value) => {
    if (isItSelectable && setSelected) setSelected(value);
  };

  const openCreate = () => {
    setEditingAddress(null);
    setShowModal(true);
  };

  const openEdit = (addr) => {
    setEditingAddress(addr);
    setShowModal(true);
  };

  const handleDeleteClick = (e, id) => {
    e.stopPropagation();
    setAddressToDelete(id);
    setShowConfirmDelete(true);
  };

  const confirmDelete = async () => {
    if (!addressToDelete) return;
    await dispatch(deleteFiscalAddress(addressToDelete));
    await dispatch(getFiscalAddressList(userInfo.id));
    setShowConfirmDelete(false);
    setAddressToDelete(null);
  };

  const colSpan = Math.max(1, Math.min(12, Math.floor(12 / cardsPerRow)));

  return (
    <Container>
      {showConfirmDelete && <ModalOverlay />}

      {!hasAny && (
        <>
          <EmptyState>Ainda não tens nenhum perfil fiscal criado.</EmptyState>
          <Grid>
            <Col xs={12} sm={colSpan}>
              <AddCard onClick={openCreate}>
                <BigPlus icon={faPlus} />
              </AddCard>
            </Col>
          </Grid>
        </>
      )}

      {hasAny && (
        <>
          <ScrollContainer>
            <Grid>
              {getFiscalAdress.map((addr) => {
                const hasSelector = !!isItSelectable;

                return (
                  <Col
                    key={addr.id}
                    xs={12}
                    sm={colSpan}
                    onClick={() => handleSelection(addr.id)}
                  >
                    {variant === "simple" ? (
                      <SimpleBlock>
                        <SimpleRow>
                          <SimpleLabel>Nome:</SimpleLabel>
                          {addr.name || "—"}
                        </SimpleRow>
                        <SimpleRow>
                          <SimpleLabel>NIF:</SimpleLabel>
                          {addr.fiscal_number || "—"}
                        </SimpleRow>
                        <SimpleRow>
                          <SimpleLabel>Morada:</SimpleLabel>
                          {addr.address || "—"}
                        </SimpleRow>
                        <SimpleRow>
                          <SimpleLabel>Código Postal:</SimpleLabel>
                          {addr.postal_code || "—"}
                        </SimpleRow>
                        <SimpleRow>
                          <SimpleLabel>País:</SimpleLabel>
                          {addr.country || "—"}
                        </SimpleRow>
                        {addr.floor_number ? (
                          <SimpleRow>
                            <SimpleLabel>Info adicional:</SimpleLabel>
                            {addr.floor_number}
                          </SimpleRow>
                        ) : null}

                        <div style={{ textAlign: "right" }}>
                          <ModernButton
                            color="#de8c3a"
                            fontSize="13px"
                            style={{ marginTop: 8 }}
                            onClick={(e) => {
                              e.stopPropagation();
                              openEdit(addr);
                            }}
                          >
                            Editar
                          </ModernButton>
                        </div>
                      </SimpleBlock>
                    ) : (
                      <CardBox
                        isItSelectable={isItSelectable}
                        isSelected={currentSelected === addr.id}
                      >
                        {hasSelector && <SelectCircle />}
                        {hasSelector && currentSelected === addr.id && (
                          <CheckIcon icon={faCircleCheck} />
                        )}

                        <Actions>
                          <IconButton
                            title="Editar"
                            aria-label="Editar"
                            onClick={(e) => {
                              e.stopPropagation();
                              openEdit(addr);
                            }}
                          >
                            <FontAwesomeIcon icon={faPen} />
                          </IconButton>
                          <IconButton
                            title="Remover"
                            aria-label="Remover"
                            onClick={(e) => handleDeleteClick(e, addr.id)}
                          >
                            <FontAwesomeIcon icon={faXmark} />
                          </IconButton>
                        </Actions>

                        <Name $hasSelector={hasSelector}>{addr.name}</Name>
                        <Line $hasSelector={hasSelector}>{addr.address}</Line>
                        <Line $hasSelector={hasSelector}>
                          {addr.postal_code}
                        </Line>
                        <Line $hasSelector={hasSelector}>{addr.country}</Line>
                        <Line $hasSelector={hasSelector}>
                          {addr.fiscal_number}
                        </Line>
                      </CardBox>
                    )}
                  </Col>
                );
              })}

              {/* AddCard only in default variant */}
              {variant === "default" && (
                <Col xs={12} sm={colSpan}>
                  <AddCard
                    onClick={(e) => {
                      e.stopPropagation();
                      openCreate();
                    }}
                  >
                    <BigPlus icon={faPlus} />
                  </AddCard>
                </Col>
              )}
            </Grid>
          </ScrollContainer>
        </>
      )}

      <CustomModal
        show={showModal}
        onHide={() => setShowModal(false)}
        title={editingAddress ? "Editar perfil fiscal" : "Novo perfil fiscal"}
        centered={false}
      >
        {userInfo?.id && (
          <FiscalAddressForm
            user_id={userInfo.id}
            dispatch={dispatch}
            getFiscalAddressList={getFiscalAddressList}
            closeModal={() => setShowModal(false)}
            initialData={editingAddress} // <-- prefill on edit
          />
        )}
      </CustomModal>

      <ConfirmationModal
        show={showConfirmDelete}
        onHide={() => setShowConfirmDelete(false)}
        onConfirm={confirmDelete}
        title="Remover perfil fiscal"
        body="Tem certeza que deseja remover este perfil fiscal?"
        confirmLabel="Remover"
        confirmColor="#ff704d"
        cancelLabel="Cancelar"
        cancelColor="#99b0ad"
        centered={false}
        topSpacing={"250px auto"}
      />
    </Container>
  );
}

export default FiscalAdress;
